import React, { useEffect, useState } from 'react'
import {
  List,
  ListItem,
  Button,
  Typography,
  IconButton,
  Tooltip,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  MenuItem,
  DialogActions,
} from '@mui/material'
import { authedAxios } from '../../utils'
import { grey } from '@mui/material/colors'
import axios from 'axios'
import useAlert from '../../hooks/useAlert'
import { Delete, MoveDown } from '@mui/icons-material'
import DeleteConfirmModal from '../../customer/components/DeleteConfirmModal'
import useIsAdmin from '../../hooks/useIsAdmin'

const DocumentList = ({
  documents,
  removeDocument,
  setDocument,
  documentCategoryDropdown,
}) => {
  const { createAlert, createGenericErrorAlert } = useAlert()

  const [deletingFile, setDeletingFile] = useState(null)
  const [changingCategory, setChangingCategory] = useState(null)

  const getAndDownloadFile = ({ s3Key, fileName }) => {
    return authedAxios.get(`/api/document/${s3Key}`).then(({ data: url }) => {
      if (!url) {
        return createAlert({
          message: "This file doesn't seem to exist anymore",
          severity: 'error',
        })
      }

      return window.open(url, '_blank')
    })
  }

  const deleteDocument = (document) => {
    const { s3Key, fileName } = document

    return authedAxios
      .delete(`/api/document/${s3Key}`)
      .then(() => {
        createAlert({ message: 'Successfully deleted ' + fileName })
        removeDocument(document)
      })
      .catch(() => {
        createGenericErrorAlert()
      })
  }

  const { isAdmin } = useIsAdmin()

  return (
    <>
      {documents.length === 0 && (
        <Typography sx={{ padding: '16px 24px' }}>
          <i>No documents in this category</i>
        </Typography>
      )}
      <List>
        {documents.map((d, i) => {
          const date = new Date(d.modifiedOn).toLocaleDateString()

          const split = d.s3Key.split('.')
          const ext = split[split.length - 1]

          return (
            <ListItem
              key={i}
              style={{
                display: 'block',
                ml: 3,
                pb: 2,
                backgroundColor: i % 2 === 0 ? grey[100] : 'white',
              }}
            >
              <Button
                variant='text'
                onClick={() => getAndDownloadFile(d)}
                sx={{ ml: -3 }}
              >
                <span className={`fiv-viv fiv-icon-${ext}`}></span>
                &nbsp;
                {d.fileName}
                &nbsp;
              </Button>
              <i style={{ fontSize: '0.875rem' }}>Last updated: {date}</i>

              <Tooltip title='Change category'>
                <IconButton
                  sx={{ ml: 1 }}
                  onClick={() => setChangingCategory(d)}
                >
                  <MoveDown />
                </IconButton>
              </Tooltip>

              {isAdmin && (
                <IconButton onClick={() => setDeletingFile(d)}>
                  <Delete color='warning' />
                </IconButton>
              )}
            </ListItem>
          )
        })}
      </List>

      <DeleteConfirmModal
        isOpen={!!deletingFile}
        deleteString={deletingFile?.fileName || ''}
        onClose={() => setDeletingFile(null)}
        onSubmit={() => {
          deleteDocument(deletingFile)
          setDeletingFile(null)
        }}
      ></DeleteConfirmModal>

      <ChangeCategoryModal
        isOpen={!!changingCategory}
        document={changingCategory}
        setDocument={setDocument}
        documentCategoryDropdown={documentCategoryDropdown}
        onClose={() => setChangingCategory(null)}
      />
    </>
  )
}

const ChangeCategoryModal = ({
  isOpen,
  document,
  setDocument,
  documentCategoryDropdown,
  onClose,
}) => {
  const [categoryTypeId, setCategoryTypeId] = useState(document?.categoryTypeId)

  const { createAlert, createGenericErrorAlert } = useAlert()

  useEffect(() => {
    setCategoryTypeId(document?.categoryTypeId)
  }, [JSON.stringify(document)])

  const onSave = () => {
    const resolvedId =
      categoryTypeId === 'uncategorized' ? null : categoryTypeId
    authedAxios
      .put(`/api/document/category/${resolvedId}`, document)
      .then(() => {
        onClose()
        setDocument({
          ...document,
          categoryTypeId: resolvedId,
        })
        setTimeout(() => {
          createAlert({ message: 'Successfully changed categories' })
        }, [])
      })
      .catch((e) => {
        onClose()

        setTimeout(() => {
          createGenericErrorAlert()
        }, [])
      })
  }

  return (
    <Dialog open={isOpen}>
      <DialogTitle>Change category for {document?.fileName}</DialogTitle>
      <DialogContent>
        <TextField
          select
          fullWidth
          value={categoryTypeId || 'uncategorized'}
          onChange={(e) => setCategoryTypeId(e.target.value)}
        >
          <MenuItem value={'uncategorized'}>Uncategorized</MenuItem>
          {documentCategoryDropdown?.options?.map((option) => (
            <MenuItem value={option.id} key={option.id}>
              {option.name}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()}>Cancel</Button>
        <Button onClick={() => onSave()}>Save</Button>
      </DialogActions>
    </Dialog>
  )
}

export default DocumentList
